import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Eye, Search, ClipboardList, Loader2, AlertCircle, X, Filter, Printer
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';
import { ProductAutocomplete } from './forms/ProductAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [fVendorId, setFVendorId] = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fCompanyInput, setFCompanyInput] = useState('');
  const [showCompanySuggest, setShowCompanySuggest] = useState(false);
  const [fStatus, setFStatus] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fActivePreset, setFActivePreset] = useState('');
  const [applied, setApplied] = useState({ vendorId: '', company: '', status: '', dateFrom: '', dateTo: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const [ordRes, prodsRes, vRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-orders`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
      ]);
      setOrders(ordRes.data); setVendors(vRes.data || []);
      const map = {}; (prodsRes.data || []).forEach(p => { map[p.id] = p.name; }); setProductMap(map);
    } catch (err) { setError('Failed to fetch purchase orders'); } finally { setLoading(false); }
  };

  const companyNames = [...new Set(orders.map(o => o.company_name).filter(Boolean))].sort();
  const companySuggestions = fCompanyInput.trim() ? companyNames.filter(n => n.toLowerCase().includes(fCompanyInput.toLowerCase())) : companyNames.slice(0, 8);

  function calcPreset(preset) {
    const now = new Date(); let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'today') { fd = fmt(now); td = fmt(now); }
    else if (preset === 'last_7_days') { const start = new Date(now); start.setDate(now.getDate() - 6); fd = fmt(start); td = fmt(now); }
    else if (preset === 'this_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); td = fmt(now); }
    else if (preset === 'last_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth()-1, 1)); td = fmt(new Date(now.getFullYear(), now.getMonth(), 0)); }
    else if (preset === 'this_quarter') { const qs = Math.floor(now.getMonth()/3)*3; fd = fmt(new Date(now.getFullYear(), qs, 1)); td = fmt(now); }
    else if (preset === 'last_quarter') { const qs = Math.floor(now.getMonth()/3)*3-3; const y = qs<0?now.getFullYear()-1:now.getFullYear(); const qss=((qs%12)+12)%12; fd=fmt(new Date(y,qss,1)); td=fmt(new Date(y,qss+3,0)); }
    else if (preset === 'this_year') { fd = fmt(new Date(now.getFullYear(), 0, 1)); td = fmt(now); }
    else if (preset === 'last_year') { fd = fmt(new Date(now.getFullYear()-1, 0, 1)); td = fmt(new Date(now.getFullYear()-1, 11, 31)); }
    return { fd, td };
  }
  const applyPreset = (preset) => {
    const { fd, td } = calcPreset(preset);
    setFActivePreset(preset);
    setFDateFrom(fd);
    setFDateTo(td);
    setApplied({ vendorId: fVendorId, company: fCompany, status: fStatus, dateFrom: fd, dateTo: td });
  };
  const applyFilters = () => setApplied({ vendorId: fVendorId, company: fCompany, status: fStatus, dateFrom: fDateFrom, dateTo: fDateTo });
  const clearFilters = () => { setFVendorId(''); setFCompany(''); setFCompanyInput(''); setFStatus(''); setFDateFrom(''); setFDateTo(''); setFActivePreset(''); setApplied({ vendorId: '', company: '', status: '', dateFrom: '', dateTo: '' }); };
  const hasFilters = applied.vendorId || applied.company || applied.status || applied.dateFrom || applied.dateTo;

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-orders/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchOrders();
    } catch (err) {
      setError('Failed to delete purchase order');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const filtered = (() => {
    let r = orders.filter(o => (o.order_number || '').toLowerCase().includes(searchQuery.toLowerCase()) || (o.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase()));
    if (applied.vendorId) r = r.filter(o => o.vendor_id === applied.vendorId);
    if (applied.company) r = r.filter(o => o.company_name === applied.company);
    if (applied.status === 'converted') r = r.filter(o => o.converted_to_bill);
    if (applied.status === 'open') r = r.filter(o => !o.converted_to_bill && o.status !== 'Voided');
    if (applied.status === 'voided') r = r.filter(o => o.status === 'Voided');
    if (applied.dateFrom) r = r.filter(o => { const d = (o.order_date || o.created_at || '').split('T')[0]; return d >= applied.dateFrom; });
    if (applied.dateTo) r = r.filter(o => { const d = (o.order_date || o.created_at || '').split('T')[0]; return d <= applied.dateTo; });
    return r;
  })();
  const pickupOrders = filtered.filter(order => order.status !== 'Voided');

  const generatePickupSheet = () => {
    if (!pickupOrders.length) {
      setError('No active purchase orders match the current filters');
      return;
    }

    // Combine repeated products across all matching POs under each vendor.
    const grouped = new Map();
    pickupOrders.forEach(order => {
      const vendorKey = order.vendor_id || order.vendor_name || 'unknown-vendor';
      if (!grouped.has(vendorKey)) {
        grouped.set(vendorKey, { name: order.vendor_name || 'Vendor not specified', items: new Map() });
      }
      const vendorGroup = grouped.get(vendorKey);
      (order.line_items || []).forEach(item => {
        if (!item.product_id && !item.description) return;
        const itemName = productMap[item.product_id] || item.description || 'Unnamed item';
        const itemKey = item.product_id || itemName;
        const existing = vendorGroup.items.get(itemKey) || { name: itemName, quantity: 0 };
        existing.quantity += Number(item.quantity) || 0;
        vendorGroup.items.set(itemKey, existing);
      });
    });

    const vendorsForSheet = [...grouped.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(vendor => ({
        ...vendor,
        sortedItems: [...vendor.items.values()].sort((a, b) => a.name.localeCompare(b.name)),
      }));

    const dateRange = applied.dateFrom || applied.dateTo
      ? `Order date: ${applied.dateFrom || 'Any'} to ${applied.dateTo || 'Any'}`
      : 'Order date: All dates';
    const filterParts = [];
    if (applied.company) filterParts.push(`Company: ${applied.company}`);
    if (applied.status) filterParts.push(`Status: ${{ converted: 'Converted', open: 'Open', voided: 'Voided' }[applied.status] || applied.status}`);
    if (searchQuery.trim()) filterParts.push(`Search: ${searchQuery.trim()}`);

    const width = 1400;
    const padding = 90;
    const contentWidth = width - padding * 2;
    const measureCanvas = document.createElement('canvas');
    const measure = measureCanvas.getContext('2d');
    const wrapText = (context, text, maxWidth) => {
      const words = String(text).split(/\s+/);
      const lines = [];
      let line = '';
      words.forEach(word => {
        const candidate = line ? `${line} ${word}` : word;
        if (line && context.measureText(candidate).width > maxWidth) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      });
      if (line) lines.push(line);
      return lines.length ? lines : [''];
    };

    measure.font = '28px Arial';
    const measuredVendors = vendorsForSheet.map(vendor => ({
      ...vendor,
      measuredItems: vendor.sortedItems.map(item => {
        const quantity = Number.isInteger(item.quantity)
          ? String(item.quantity)
          : item.quantity.toLocaleString('en-IN', { maximumFractionDigits: 2 });
        const text = `- ${item.name} - ${quantity}`;
        return { text, lines: wrapText(measure, text, contentWidth - 45) };
      }),
    }));
    const bodyHeight = measuredVendors.reduce((sum, vendor) =>
      sum + 64 + (vendor.measuredItems.length
        ? vendor.measuredItems.reduce((itemSum, item) => itemSum + item.lines.length * 38 + 8, 0)
        : 46) + 24, 0);
    const headerHeight = filterParts.length ? 260 : 220;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = Math.max(500, headerHeight + bodyHeight + 70);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let y = 90;
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 42px Arial';
    ctx.fillText('PURCHASE ORDER PICKUP SHEET', padding, y);
    y += 55;
    ctx.fillStyle = '#64748b';
    ctx.font = '22px Arial';
    ctx.fillText(`Generated: ${new Date().toLocaleString('en-IN')}`, padding, y);
    y += 34;
    ctx.fillText(`${dateRange} | Purchase orders: ${pickupOrders.length}`, padding, y);
    y += 34;
    if (filterParts.length) {
      ctx.fillText(filterParts.join(' | '), padding, y);
      y += 38;
    }
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, y);
    ctx.lineTo(width - padding, y);
    ctx.stroke();
    y += 48;

    measuredVendors.forEach(vendor => {
      ctx.fillStyle = '#fff7ed';
      ctx.fillRect(padding - 18, y - 38, contentWidth + 36, 58);
      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 32px Arial';
      ctx.fillText(vendor.name, padding, y);
      y += 54;
      ctx.font = '28px Arial';
      if (!vendor.measuredItems.length) {
        ctx.fillStyle = '#64748b';
        ctx.fillText('- No items', padding + 28, y);
        y += 46;
      } else {
        vendor.measuredItems.forEach(item => {
          ctx.fillStyle = '#334155';
          item.lines.forEach(line => {
            ctx.fillText(line, padding + 28, y);
            y += 38;
          });
          y += 8;
        });
      }
      y += 24;
    });

    const fileDate = new Date().toISOString().split('T')[0];
    canvas.toBlob(blob => {
      if (!blob) {
        setError('Failed to generate pickup sheet image');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase-order-pickup-sheet-${fileDate}.png`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div data-testid="purchase-orders-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Purchase Orders</h1>
          <p className="text-slate-600 mt-1">Manage purchase orders</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={generatePickupSheet} disabled={loading || pickupOrders.length === 0} data-testid="generate-pickup-sheet-btn"
            className="inline-flex items-center gap-2 border border-orange-500 text-orange-600 font-bold tracking-wider uppercase px-5 py-3 rounded-none hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Printer className="h-5 w-5" /> Generate Pickup Sheet
          </button>
          {perms.canAdd && (
          <button onClick={() => navigate('/admin/purchase/orders/new')} data-testid="create-order-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
            <Plus className="h-5 w-5" /> Create Purchase Order
          </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by order number or vendor..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input" className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3"><Filter className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>{hasFilters && <button onClick={clearFilters} data-testid="clear-filters-btn" className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Vendor</label><ProductAutocomplete value={fVendorId} onChange={setFVendorId} products={vendors} testId="filter-vendor" placeholder="Type vendor..." /></div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <div className="relative">
              <input type="text" value={fCompanyInput} onChange={e => { setFCompanyInput(e.target.value); setFCompany(''); setShowCompanySuggest(true); }} onFocus={() => setShowCompanySuggest(true)} onBlur={() => setTimeout(() => setShowCompanySuggest(false), 150)} placeholder="Type company..." data-testid="filter-company-input" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
              {fCompanyInput && <button onClick={() => { setFCompany(''); setFCompanyInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>}
              {showCompanySuggest && companySuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
                  {companySuggestions.map(name => (
                    <button key={name} onMouseDown={() => { setFCompany(name); setFCompanyInput(name); setShowCompanySuggest(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${fCompany === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>{name}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Status</label><select value={fStatus} onChange={e => setFStatus(e.target.value)} data-testid="filter-status" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white"><option value="">All</option><option value="open">Open</option><option value="converted">Converted</option><option value="voided">Voided</option></select></div>
          <div><button onClick={applyFilters} data-testid="apply-filters-btn" className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">Apply</button></div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Order Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[{ key: 'today', label: 'Today' }, { key: 'last_7_days', label: 'Last 7 Days' }, { key: 'this_month', label: 'This Month' }, { key: 'last_month', label: 'Last Month' }, { key: 'this_quarter', label: 'This Quarter' }, { key: 'last_quarter', label: 'Last Quarter' }, { key: 'this_year', label: 'This Year' }, { key: 'last_year', label: 'Last Year' }].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`} className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${fActivePreset === p.key ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'}`}>{p.label}</button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label><input type="date" value={fDateFrom} onChange={e => { setFDateFrom(e.target.value); setFActivePreset(''); }} data-testid="date-from" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium">To</label><input type="date" value={fDateTo} onChange={e => { setFDateTo(e.target.value); setFActivePreset(''); }} data-testid="date-to" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <ClipboardList className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No purchase orders yet</h3>
            <p className="text-slate-500 mb-6">Create your first purchase order</p>
            <button onClick={() => navigate('/admin/purchase/orders/new')}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Create First Order
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-8 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Order Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Order No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Due Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Total</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((order) => (
              <div key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                <div data-testid={`order-row-${order.id}`}
                  className="grid grid-cols-1 md:grid-cols-8 gap-4 px-6 pt-4 items-center">
                  <div className="text-slate-600 text-sm">{formatDate(order.order_date || order.created_at)}</div>
                  <div className="col-span-2 font-mono font-medium text-slate-900">{order.order_number || '-'}</div>
                  <div className="text-slate-700 text-sm">{order.vendor_name || '-'}</div>
                  <div className="text-slate-600 text-sm">{formatDate(order.due_date)}</div>
                  <div className="font-medium text-slate-900">₹{order.total?.toLocaleString()}</div>
                  <div className="text-sm">
                    {order.status === 'Voided' ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-bold uppercase rounded bg-red-100 text-red-700" data-testid={`voided-badge-${order.id}`} title={order.void_reason || ''}>
                        Voided
                      </span>
                    ) : order.converted_to_bill ? (
                      order.purchase_bill_id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-1 text-xs font-bold uppercase rounded bg-green-100 text-green-700">
                            Converted
                          </span>
                          {order.purchase_bill_number && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/admin/purchase/bills/${order.purchase_bill_id}`); }}
                              data-testid={`converted-link-${order.id}`}
                              className="text-xs font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                            >
                              ({order.purchase_bill_number})
                            </button>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-bold uppercase rounded bg-green-100 text-green-700" data-testid={`converted-badge-${order.id}`}>
                          Converted
                        </span>
                      )
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium uppercase rounded bg-slate-100 text-slate-600" data-testid={`open-badge-${order.id}`}>
                        Open
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/admin/purchase/orders/${order.id}`)} data-testid={`view-btn-${order.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                      <Eye className="h-4 w-4" />
                    </button>
                    {perms.canDelete && order.status !== 'Voided' && (
                    <button onClick={() => setDeleteConfirm(order.id)} data-testid={`delete-btn-${order.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this purchase order?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
